import startCase from 'lodash/startCase';
import map from 'lodash/map';
import UniversalContainer from '../universal-container';
import {
  boolCheckboxSpec,
  repeatable,
  stringInputSpec,
  stringSelectSpec,
  group,
} from '../../lib/editing-configs';
import { PRODUCT_TYPE_UNSTRUCTURED_VERTICAL } from './utils';

import { DATA_DEPENDENCY_TYPES } from '../../lib/constants';

export default (componentDocument) => {
  const isUnstructuredVertical =
    componentDocument?.props?.isUnstructuredVertical;
  const isMortgagesType =
    componentDocument?.dataDependency?.type === DATA_DEPENDENCY_TYPES.MORTGAGES;
  const isInsuranceType =
    componentDocument?.dataDependency?.type === DATA_DEPENDENCY_TYPES.INSURANCE;
  const headingInputConfig = stringInputSpec(
    'heading',
    'Product Name Override'
  );

  // in order to Output Cta below KeyFacts inn mobile view ports we look for an
  // element with an element with "apply-now" class, which currently is not
  // available for "INSURANCE" and "MORTGAGES" types so well disable this option
  // for the time being
  const disableOutputCtaBelowKeyFactsInMobileViewPorts =
    isMortgagesType || isInsuranceType;

  const dataDependencyIdInputConfig = stringInputSpec(
    'dataDependency.data.ids.0.id',
    'Offer Id',
    { isProp: false }
  );

  const dataDependencyDriver = stringSelectSpec(
    'driver',
    'Driver',
    [
      { label: 'DEFAULT DRIVER GROUP', value: 'DEFAULT_DRIVER_GROUP' },
      { label: 'APR Group', value: 'APR_GROUP' },
    ],
    {
      help: `Defaults to "DEFAULT_DRIVER_GROUP"`,
    }
  );

  const dataDependencyDriverOverrides = boolCheckboxSpec(
    'driverOverrides',
    'Driver Overrides?'
  );
  const overrideProductNameInputConfig = componentDocument?.props
    ?.overrideProductName
    ? headingInputConfig
    : [];

  const ctaTextConfig = stringInputSpec('ctaText', 'CTA text');
  const keyFactsInputConfig = repeatable(
    'keyFacts',
    'Key Facts',
    'keyFact',
    () => [
      stringInputSpec('label', 'Key Fact Label'),
      stringInputSpec('text', 'Key Fact Text'),
      stringInputSpec('tooltip', 'Key Fact Tooltip'),
    ]
  );

  const overrideCtaTextInputConfig = componentDocument?.props?.overrideCtaText
    ? ctaTextConfig
    : [];

  const driverOverridesKeyFacts = componentDocument?.props?.driverOverrides
    ? keyFactsInputConfig
    : [];

  const dataDependencyConfig = [
    dataDependencyIdInputConfig,
    boolCheckboxSpec('overrideProductName', 'Override Product Name?'),
    overrideProductNameInputConfig,
    boolCheckboxSpec('overrideCtaText', 'Override CTA text?'),
    overrideCtaTextInputConfig,
    dataDependencyDriver,
    dataDependencyDriverOverrides,
    driverOverridesKeyFacts,
  ];

  const unstructuredVerticalConfig = [
    stringSelectSpec(
      'productType',
      'Product type',
      map(PRODUCT_TYPE_UNSTRUCTURED_VERTICAL, (productType) => ({
        label: startCase(productType),
        value: productType,
      }))
    ),
    headingInputConfig,
    stringInputSpec('productCardImage', 'Product card image'),
    stringInputSpec('ctaHref', 'CTA href'),
    ctaTextConfig,
    stringInputSpec('ctaSubtext', 'CTA subtext'),
    keyFactsInputConfig,
  ];

  const outputCtaBelowKeyFactsInMobileViewPortsInputConfig =
    disableOutputCtaBelowKeyFactsInMobileViewPorts
      ? []
      : [
          boolCheckboxSpec(
            'outputCtaBelowKeyFactsInMobileViewPorts',
            'Output CTA Below Key Facts In Mobile Viewports?'
          ),
        ];

  const includeWhyThisProduct = componentDocument?.props?.includeWhyThisProduct;
  const whyThisProductConfig = [
    boolCheckboxSpec(
      'includeWhyThisProduct',
      "Include 'Why this product' section?",
      {
        help: 'Include a section below the product card explaining why this product is a Best of Award',
      }
    ),
    ...(includeWhyThisProduct
      ? [
          stringInputSpec('whyThisProductText', 'Product explanation', {
            help: 'Short text explaining why this product made the list',
          }),
          stringInputSpec('whyThisProductMethodologyUrl', 'Methodology URL'),
          stringInputSpec(
            'whyThisProductTypeOverride',
            'Override product type',
            {
              help: "Override the category name to use in the 'Why this product' title",
            }
          ),
        ]
      : []),
  ];

  const includeWhyNerdsLoveIt = componentDocument?.props?.includeWhyNerdsLoveIt;
  const whyNerdsLoveItConfig = [
    boolCheckboxSpec(
      'includeWhyNerdsLoveIt',
      "Include 'Why our Nerds Love It' section?",
      {
        help: 'Include a section below the product card explaining why our Nerds Love it',
      }
    ),
    boolCheckboxSpec('turnOnGroupedBorders', 'Turn On Grouped Borders'),
    ...(includeWhyNerdsLoveIt
      ? [
          ...UniversalContainer.getEditingConfig(
            componentDocument,
            'Footer Container',
            ['backgroundColor', 'backgroundImage', 'margin', 'padding'],
            'whyNerdsLoveItContainer'
          ),
          stringInputSpec('whyNerdsLoveItTitle', 'Title'),
          stringInputSpec('whyNerdsLoveItText', 'Explanation', {
            help: 'Short text explaining why our Nerds Love It',
          }),
          stringInputSpec('whyNerdsLoveItAvatarUrl', 'Avatar URL'),
          stringInputSpec('whyNerdsLoveItTitleIconUrl', 'Title Icon URL', {
            help: 'Icon o be output after the title line.',
          }),
          stringInputSpec('whyNerdsLoveItMethodologyUrl', 'Methodology URL'),
        ]
      : []),
  ];

  const queryParamsConfig = [
    boolCheckboxSpec('filterByQueryParam', 'Reflect on query params changes?', {
      help: 'If a user uses a different query param we will hide the card',
    }),
    ...(componentDocument?.props?.filterByQueryParam
      ? [
          stringInputSpec('queryParamName', 'Query param name', {
            help: 'Which query param we should use. For multilevel filter use  "filterCategory|filterOption"',
          }),
          stringInputSpec('queryParamValue', 'Query param value', {
            help: `
                The list of the possible values separated by |. When a queryParam with the queryParamName
                contains one of the possible values we show the entity.
              `,
          }),
        ]
      : []),
  ];

  const includeBadge = componentDocument?.props?.includeBadge;
  const badgeConfig = [
    boolCheckboxSpec('includeBadge', 'Include Best-of Badge?'),
    includeBadge && stringInputSpec('badgeText', 'Badge Text'),
  ];

  return [
    boolCheckboxSpec(
      'disableDefaultProductCard',
      'Disable Default Product Card?'
    ),
    ...UniversalContainer.getEditingConfig(
      componentDocument,
      'Container',
      [
        'backgroundColor',
        'backgroundImage',
        'border',
        'margin',
        'padding',
        'hide',
      ],
      'container'
    ),
    group('Product info', [
      boolCheckboxSpec(
        'isUnstructuredVertical',
        'Is This An Unstructured Vertical?',
        {
          help: 'Check to manually input information for a vertical without data in marketplace (Taxes, Travel, Auto Insurance)',
        }
      ),
      // Show 1. unstructured vertical fields or 2. data dependency fields.
      ...(isUnstructuredVertical
        ? unstructuredVerticalConfig
        : dataDependencyConfig),
      ...outputCtaBelowKeyFactsInMobileViewPortsInputConfig,
    ]),
    group('Ratings + badges', [
      !isUnstructuredVertical &&
        boolCheckboxSpec('omitRating', 'Omit star rating?'),
      isUnstructuredVertical && stringInputSpec('starRating', 'Star rating'),
      ...badgeConfig,
    ]),

    group('Card Footer', [...whyThisProductConfig, ...whyNerdsLoveItConfig]),
    group('Jump link', [
      stringInputSpec('identifier', 'Jump link ID', {
        help: 'You can specify a unique Jump Link ID for this product card to navigate directly to the spot on the page where this card appears',
      }),
    ]),
    group('Filters', [...queryParamsConfig]),
  ];
};
